import { Injectable, inject } from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ReservationWithHint, injectCurrentUser, isAllDayEvent } from '@assist/shared/data';
import { NewReservationDialogComponent } from '../components/new-reservation-dialog/new-reservation-dialog.component';
import {
  NewReservationCreateDialogData,
  NewReservationDialogData,
  NewReservationDialogMode,
  NewReservationDialogResult,
} from '../components/new-reservation-dialog/new-reservation-dialog.data';
import { ReservationDetailComponent } from '../components/reservation-detail/reservation-detail.component';
import { ReservationDetailModalData } from '../components/reservation-detail/reservation-detail-modal.data';

export type ReservationDialogResult = void | NewReservationDialogResult;

export type ReservationDialogOpenConfig = {
  data: ReservationDetailModalData;
  panelClass?: string | string[];
};

export type NewReservationDialogOpenConfig = {
  data: NewReservationCreateDialogData;
  panelClass?: string | string[];
};

export function mapReservationDetailToNewDialogData(data: ReservationDetailModalData): NewReservationDialogData {
  if (data.mode === 'new') {
    return {
      mode: 'new',
      closeOnSave: data.closeOnSave,
      creationWorkflowNotifications: data.creationWorkflowNotifications,
      forceReadonly: data.forceReadonly,
      readonlyOther: data.readonlyOther,
      allowCompact: data.allowCompact,
      initialEnd: data.reservation.end?.utcDateTime,
      initialIsAllDay: isAllDayReservation(data.reservation),
      initialBodyHtml: data.reservation.bodyHtml ?? '',
      initialOptionalAttendeeEmails: attendeeEmails(data.reservation, false),
      initialRequiredAttendeeEmails: attendeeEmails(data.reservation, true),
      initialResourceIds: resourceIds(data.reservation),
      initialStart: data.reservation.start?.utcDateTime,
      initialSubject: data.reservation.subject,
    };
  }

  return {
    mode: resolveExistingMode(data),
    closeOnSave: data.closeOnSave,
    creationWorkflowNotifications: data.creationWorkflowNotifications,
    eventId: eventIdForDetail(data.reservation),
    forceReadonly: data.forceReadonly,
    readonlyOther: data.readonlyOther,
    allowCompact: data.allowCompact,
    occurrenceId: data.reservation.occurrenceId,
    ownerEmail: ownerEmail(data.reservation),
    resourceIds: resourceIds(data.reservation),
    seriesMasterId: data.reservation.seriesMasterId,
  };
}

@Injectable({ providedIn: 'root' })
export class ReservationDialogOpener {
  private readonly dialog = inject(Dialog);
  private readonly currentUser = injectCurrentUser();

  open(config: ReservationDialogOpenConfig): DialogRef<ReservationDialogResult, unknown> {
    if (!this.currentUser()?.data?.features.assistNewReservationView) {
      return this.dialog.open<ReservationDialogResult, ReservationDetailModalData>(ReservationDetailComponent, config);
    }

    return this.dialog.open<ReservationDialogResult, NewReservationDialogData>(NewReservationDialogComponent, {
      ...config,
      panelClass: withNewReservationDialogPanelClass(config.panelClass),
      data: mapReservationDetailToNewDialogData(config.data),
    });
  }

  openNew(config: NewReservationDialogOpenConfig): DialogRef<ReservationDialogResult, unknown> {
    return this.dialog.open<ReservationDialogResult, NewReservationDialogData>(NewReservationDialogComponent, {
      ...config,
      panelClass: withNewReservationDialogPanelClass(config.panelClass),
    });
  }
}

function withNewReservationDialogPanelClass(panelClass: string | string[] | undefined): string[] {
  const existingPanelClasses = Array.isArray(panelClass) ? panelClass : panelClass ? [panelClass] : [];

  return [...existingPanelClasses, 'new-reservation-dialog-panel'];
}

function resolveExistingMode(data: ReservationDetailModalData): NewReservationDialogMode {
  return data.triggerChange && !data.forceReadonly ? 'edit' : 'detail';
}

function eventIdForDetail(
  reservation: Pick<ReservationWithHint, 'id' | 'type' | 'ownerEventId' | 'seriesMasterId'>
): string {
  const isOccurrenceLike = reservation.type === 'occurrence' || reservation.type === 'exception';
  return (isOccurrenceLike ? reservation.seriesMasterId : (reservation.ownerEventId ?? reservation.id)) ?? '';
}

function ownerEmail(reservation: Pick<ReservationWithHint, 'organizer'>): string {
  return reservation.organizer?.emailAddress.address ?? '';
}

function resourceIds(reservation: Pick<ReservationWithHint, 'resourceAttendees'>): number[] {
  return reservation.resourceAttendees
    .map((resourceAttendee) => resourceAttendee.resource?.idForFiltering)
    .filter((resourceId): resourceId is number => resourceId != null);
}

function attendeeEmails(reservation: Pick<ReservationWithHint, 'attendees'>, required: boolean): string[] {
  return (reservation.attendees ?? [])
    .filter((attendee) => attendee.required === required)
    .map((attendee) => attendee.emailAddress.address)
    .filter((email): email is string => !!email);
}

function isAllDayReservation(reservation: Pick<ReservationWithHint, 'start' | 'end'>): boolean {
  return isAllDayEvent(reservation.start.utcDateTime, reservation.end.utcDateTime);
}
